// src/pages/Dashboard.js
// Navigation :
//  • En-tête ligne 1 : logo · association active (menu : changer / créer) · mon profil
//  • En-tête ligne 2 : tombola active (menu : changer / créer) · plan Starter/Pro
//  • Onglets de la tombola : Billets · Lots · Tirage · Rapport · Équipe · Paramètres
import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTombola } from "../contexts/TombolaContext";
import { useProfil } from "../contexts/ProfilContext";
import { useOrg } from "../contexts/OrgContext";
import { creerTombola } from "../services/db";
import Parametres from "../components/Parametres";
import GestionBillets from "../components/GestionBillets";
import GestionLots from "../components/GestionLots";
import Tirage from "../components/Tirage";
import Rapport from "../components/Rapport";
import Equipe from "../components/Equipe";
import MonProfil from "../components/MonProfil";
import Bienvenue from "../components/Bienvenue";
import InstallBanner from "../components/InstallBanner";
import NetworkStatus from "../components/NetworkStatus";

export default function Dashboard() {
  const auth = useAuth();
  const { isAdmin, orgId, orgs, switchOrg, creerNouvelleOrg, currentUser } = auth;
  const {
    tombolas, activeTombola, activeTombolaId, setActiveTombolaId,
    loading: tombolaLoading, isPro, isAdminTombola,
  } = useTombola();
  const { monPseudo } = useProfil();
  const { organisation } = useOrg();

  const [activeTab, setActiveTab] = useState("billets");
  const [menu, setMenu] = useState(null); // null | "asso" | "tombola"
  const [modal, setModal] = useState(null); // null | "asso" | "tombola"
  const [showDashboard, setShowDashboard] = useState(false);
  const prevOrgId = React.useRef(null);
  const boulierActif = React.useRef(sessionStorage.getItem("boulier_en_cours") === "true");

  // Droits de gestion : admin de l'association OU admin de la tombola active
  const peutGerer = isAdmin || isAdminTombola;

  React.useEffect(() => {
    // Changement d'association : on repart de l'onglet Billets, assistant annulé
    if (prevOrgId.current !== null && prevOrgId.current !== orgId) {
      sessionStorage.removeItem("boulier_en_cours");
      boulierActif.current = false;
      setActiveTab("billets");
    }
    prevOrgId.current = orgId;

    // On attend la liste des tombolas de CETTE association avant de décider
    if (tombolaLoading) return;

    // Assistant de création en cours : on le laisse aller au bout
    if (boulierActif.current) return;

    // Assistant de création uniquement pour un admin d'association sans aucune tombola.
    // Un bénévole ne doit JAMAIS voir l'assistant de création.
    setShowDashboard(tombolas.length > 0 || !isAdmin);
  }, [tombolaLoading, tombolas.length, orgId, isAdmin]);

  const TABS = [
    { id: "billets", label: "Billets", icon: "🎟️" },
    { id: "lots", label: "Lots", icon: "🎁" },
    ...(peutGerer ? [
      { id: "tirage", label: "Tirage", icon: "🎰" },
      { id: "rapport", label: "Rapport", icon: "📊" },
      { id: "equipe", label: "Équipe", icon: "👥" },
      { id: "parametres", label: "Paramètres", icon: "⚙️" },
    ] : []),
  ];

  const activesTombolas = tombolas.filter((t) => !t.archived);
  const archivedTombolas = tombolas.filter((t) => t.archived);
  const nomAsso = organisation?.nom || orgs.find((o) => o.org.id === orgId)?.org.nom || "Mon association";

  function ouvrir(onglet) { setActiveTab(onglet); setMenu(null); }

  return (
    <div style={s.page}>
      {/* ── En-tête ── */}
      <header style={s.header}>
        <div style={s.headerContent}>
          <div style={s.headerRow1}>
            <div style={s.headerLeft}>
              <img src="/logo_tombolive.svg" alt="TomboLive" style={s.logoImg} />

              {/* Association active */}
              <div style={s.menuWrap}>
                <button onClick={() => setMenu(menu === "asso" ? null : "asso")} style={s.assoBtn}>
                  <span style={s.assoNom}>🏢 {nomAsso}</span>
                  <span style={s.chevron}>▾</span>
                </button>
                {menu === "asso" && (
                  <div style={s.dropdown}>
                    <div style={s.dropGroup}>Mes associations</div>
                    {orgs.map(({ org, role }) => (
                      <button key={org.id}
                        onClick={() => { if (org.id !== orgId) switchOrg(org.id); setMenu(null); }}
                        style={{ ...s.dropItem, ...(org.id === orgId ? s.dropItemActive : {}) }}>
                        <span style={s.dropNom}>{org.id === orgId ? nomAsso : org.nom}</span>
                        <span style={s.dropMeta}>{role === "admin" ? "👑" : "🤝"}</span>
                      </button>
                    ))}
                    <div style={s.dropDivider} />
                    <button onClick={() => { setMenu(null); setModal("asso"); }} style={s.dropCreate}>
                      + Nouvelle association
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Mon profil */}
            <button onClick={() => ouvrir("profil")}
              style={{ ...s.profilBtn, ...(activeTab === "profil" ? s.profilBtnActive : {}) }}
              title="Mon profil">
              <span style={s.profilAvatar}>{(monPseudo || "?").charAt(0).toUpperCase()}</span>
              <span style={s.profilNom}>{monPseudo}</span>
            </button>
          </div>

          <div style={s.headerRow2}>
            {/* Tombola active */}
            <div style={s.menuWrap}>
              <button onClick={() => setMenu(menu === "tombola" ? null : "tombola")} style={s.tombolaBtn}>
                <span>🎟️ {activeTombola?.nom || "Aucune tombola"}</span>
                {activeTombola?.annee && <span style={s.year}> {activeTombola.annee}</span>}
                <span style={s.chevron}>▾</span>
              </button>
              {menu === "tombola" && (
                <div style={s.dropdown}>
                  {activesTombolas.length > 0 && <div style={s.dropGroup}>Tombolas</div>}
                  {activesTombolas.map((t) => (
                    <button key={t.id}
                      onClick={() => { setActiveTombolaId(t.id); setMenu(null); if (activeTab === "profil") setActiveTab("billets"); }}
                      style={{ ...s.dropItem, ...(t.id === activeTombolaId ? s.dropItemActive : {}) }}>
                      <span style={s.dropNom}>{t.nom}</span>
                      <span style={s.dropMeta}>{t.annee}{t.plan === "pro" ? " · ⭐" : ""}</span>
                    </button>
                  ))}
                  {archivedTombolas.length > 0 && (
                    <>
                      <div style={s.dropGroup}>Archives</div>
                      {archivedTombolas.map((t) => (
                        <button key={t.id} onClick={() => { setActiveTombolaId(t.id); setMenu(null); }}
                          style={{ ...s.dropItem, ...(t.id === activeTombolaId ? s.dropItemActive : {}) }}>
                          <span style={s.dropNom}>📦 {t.nom}</span>
                          <span style={s.dropMeta}>{t.annee}</span>
                        </button>
                      ))}
                    </>
                  )}
                  {isAdmin && (
                    <>
                      <div style={s.dropDivider} />
                      <button onClick={() => { setMenu(null); setModal("tombola"); }} style={s.dropCreate}>
                        + Nouvelle tombola
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            {activeTombola && (
              <span style={isPro ? s.badgePro : s.badgeStarter}>{isPro ? "⭐ Pro" : "Starter"}</span>
            )}
          </div>
        </div>
        {/* ── Onglets de la tombola ── */}
        {showDashboard && activeTombola && (
          <div style={s.tabsBar}>
            <div style={s.tabsContent}>
              {TABS.map((tab) => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  style={{ ...s.tab, ...(activeTab === tab.id ? s.tabActive : {}) }}>
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </header>

      {menu && <div style={s.overlay} onClick={() => setMenu(null)} />}

      <NetworkStatus />
      <InstallBanner />

      {/* ── Contenu ── */}
      <main style={s.main}>
        <div style={s.card}>
          {activeTab === "profil" ? (
            <MonProfil onCreerAssociation={() => setModal("asso")} />
          ) : (
            <>
              {/* Assistant de création — jusqu'à la fin de l'onboarding */}
              {tombolaLoading && (
                <div style={{ textAlign: "center", padding: "3rem", color: "#667eea" }}>⏳ Chargement…</div>
              )}
              {!tombolaLoading && !showDashboard && (
                <Bienvenue
                  onStep1Done={() => {
                    sessionStorage.setItem("boulier_en_cours", "true");
                    boulierActif.current = true;
                  }}
                  onTermine={() => {
                    sessionStorage.removeItem("boulier_en_cours");
                    boulierActif.current = false;
                    setShowDashboard(true);
                    setActiveTab("billets");
                  }}
                />
              )}
              {!tombolaLoading && showDashboard && !activeTombola && (
                <div style={s.vide}>
                  <div style={s.videIcon}>🎟️</div>
                  <div style={s.videTitre}>Aucune tombola pour le moment</div>
                  <div style={s.videTexte}>
                    {isAdmin
                      ? "Créez votre première tombola pour commencer à vendre des billets."
                      : "L'administrateur de l'association ne vous a pas encore ajouté à une tombola."}
                  </div>
                  {isAdmin && <button onClick={() => setModal("tombola")} style={s.btnPrimary}>+ Créer une tombola</button>}
                </div>
              )}
              {!tombolaLoading && showDashboard && activeTombola && (<>
                {activeTab === "billets" && <GestionBillets />}
                {activeTab === "lots" && <GestionLots />}
                {activeTab === "tirage" && peutGerer && <Tirage />}
                {activeTab === "rapport" && peutGerer && <Rapport />}
                {activeTab === "equipe" && peutGerer && <Equipe />}
                {activeTab === "parametres" && peutGerer && <Parametres />}
              </>)}
            </>
          )}
        </div>
      </main>

      {modal === "tombola" && (
        <ModalNouvelleTombola
          onClose={() => setModal(null)}
          onCree={(id) => { setActiveTombolaId(id); setModal(null); setShowDashboard(true); setActiveTab("parametres"); }}
          orgId={orgId}
          currentUser={currentUser}
        />
      )}
      {modal === "asso" && (
        <ModalNouvelleAsso
          onClose={() => setModal(null)}
          onCree={() => { setModal(null); setActiveTab("billets"); }}
          creerNouvelleOrg={creerNouvelleOrg}
        />
      )}
    </div>
  );
}

// ─── Modale : nouvelle tombola ────────────────────────────────────────────────
function ModalNouvelleTombola({ onClose, onCree, orgId, currentUser }) {
  const [nom, setNom] = useState("");
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState("");

  async function valider(e) {
    e.preventDefault();
    if (!nom.trim()) return;
    setLoading(true); setErreur("");
    try {
      const id = await creerTombola(orgId, { nom: nom.trim(), annee: Number(annee) || new Date().getFullYear() }, currentUser.uid, currentUser.email);
      onCree(id);
    } catch {
      setErreur("Impossible de créer la tombola. Réessayez.");
      setLoading(false);
    }
  }

  return (
    <div style={m.overlay} onClick={onClose}>
      <form style={m.box} onClick={(e) => e.stopPropagation()} onSubmit={valider}>
        <h3 style={m.titre}>🎟️ Nouvelle tombola</h3>
        <label style={m.label}>Nom de la tombola</label>
        <input autoFocus value={nom} onChange={(e) => setNom(e.target.value)} maxLength={120}
          placeholder="Ex : Tombola de Noël" style={m.input} />
        <label style={m.label}>Année</label>
        <input type="number" value={annee} onChange={(e) => setAnnee(e.target.value)} min="2020" max="2099"
          style={{ ...m.input, width: "110px" }} />
        <p style={m.aide}>Chaque tombola est gratuite jusqu'à 100 billets. Vous réglerez ensuite le prix et les lots.</p>
        {erreur && <div style={m.erreur}>{erreur}</div>}
        <div style={m.actions}>
          <button type="button" onClick={onClose} style={m.btnAnnuler}>Annuler</button>
          <button type="submit" disabled={loading || !nom.trim()} style={m.btnOk}>{loading ? "Création…" : "Créer"}</button>
        </div>
      </form>
    </div>
  );
}

// ─── Modale : nouvelle association ────────────────────────────────────────────
function ModalNouvelleAsso({ onClose, onCree, creerNouvelleOrg }) {
  const [nom, setNom] = useState("");
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState("");

  async function valider(e) {
    e.preventDefault();
    if (!nom.trim()) return;
    setLoading(true); setErreur("");
    try {
      await creerNouvelleOrg(nom.trim().slice(0, 120));
      onCree();
    } catch {
      setErreur("Impossible de créer l'association. Réessayez.");
      setLoading(false);
    }
  }

  return (
    <div style={m.overlay} onClick={onClose}>
      <form style={m.box} onClick={(e) => e.stopPropagation()} onSubmit={valider}>
        <h3 style={m.titre}>🏢 Nouvelle association</h3>
        <p style={m.aide}>
          Pour une autre structure que vous gérez (club, APE, comité des fêtes…).
          Elle aura ses propres tombolas et sa propre équipe.
        </p>
        <label style={m.label}>Nom de l'association ou du club</label>
        <input autoFocus value={nom} onChange={(e) => setNom(e.target.value)} maxLength={120}
          placeholder="Ex : FC Bresse Haute Seille" style={m.input} />
        {erreur && <div style={m.erreur}>{erreur}</div>}
        <div style={m.actions}>
          <button type="button" onClick={onClose} style={m.btnAnnuler}>Annuler</button>
          <button type="submit" disabled={loading || !nom.trim()} style={m.btnOk}>{loading ? "Création…" : "Créer"}</button>
        </div>
      </form>
    </div>
  );
}

const s = {
  page: { minHeight: "100vh", background: "#f7f8fc", fontFamily: "'Segoe UI', system-ui, sans-serif" },
  header: { background: "linear-gradient(135deg,#667eea,#764ba2)", boxShadow: "0 2px 12px rgba(0,0,0,0.15)", position: "sticky", top: 0, zIndex: 100 },
  headerContent: { maxWidth: "1000px", margin: "0 auto", padding: "0.6rem 1rem" },
  headerRow1: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", marginBottom: "0.45rem" },
  headerRow2: { display: "flex", alignItems: "center", gap: "10px" },
  headerLeft: { display: "flex", alignItems: "center", gap: "8px", minWidth: 0 },
  logoImg: { height: "34px", width: "auto", objectFit: "contain", background: "white", borderRadius: "6px", padding: "2px 4px", flexShrink: 0 },
  menuWrap: { position: "relative", minWidth: 0 },
  assoBtn: { display: "flex", alignItems: "center", gap: "6px", maxWidth: "100%", background: "none", border: "none", color: "#fff", cursor: "pointer", padding: "4px 2px", fontSize: "0.95rem", fontWeight: "800" },
  assoNom: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "52vw" },
  chevron: { opacity: 0.75, fontSize: "0.8rem" },
  profilBtn: { display: "flex", alignItems: "center", gap: "6px", background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: "20px", padding: "3px 10px 3px 3px", cursor: "pointer", color: "#fff", flexShrink: 0 },
  profilBtnActive: { background: "rgba(255,255,255,0.32)" },
  profilAvatar: { width: "26px", height: "26px", borderRadius: "50%", background: "#fff", color: "#764ba2", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "800", fontSize: "0.8rem" },
  profilNom: { fontSize: "0.8rem", fontWeight: "600", maxWidth: "90px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  tombolaBtn: { display: "flex", alignItems: "center", gap: "4px", background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: "8px", padding: "5px 10px", color: "#fff", cursor: "pointer", fontSize: "0.85rem", fontWeight: "600", maxWidth: "70vw", whiteSpace: "nowrap", overflow: "hidden" },
  year: { opacity: 0.7, fontSize: "0.78rem" },
  dropdown: { position: "absolute", top: "calc(100% + 8px)", left: 0, background: "#fff", borderRadius: "12px", boxShadow: "0 8px 32px rgba(0,0,0,0.18)", minWidth: "240px", maxWidth: "88vw", zIndex: 200, overflow: "hidden", border: "1px solid #eee" },
  dropGroup: { padding: "8px 14px 4px", fontSize: "0.7rem", color: "#aaa", fontWeight: "700", textTransform: "uppercase" },
  dropItem: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", width: "100%", padding: "10px 14px", background: "none", border: "none", cursor: "pointer", fontSize: "0.88rem", color: "#333", textAlign: "left" },
  dropItemActive: { background: "#f0f4ff", color: "#4c5ee8", fontWeight: "700" },
  dropNom: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  dropMeta: { color: "#aaa", fontSize: "0.78rem", flexShrink: 0 },
  dropDivider: { height: "1px", background: "#f0f0f0", margin: "4px 0" },
  dropCreate: { display: "block", width: "100%", padding: "10px 14px", background: "none", border: "none", cursor: "pointer", fontSize: "0.88rem", color: "#667eea", fontWeight: "700", textAlign: "left" },
  badgePro: { background: "rgba(255,193,7,0.2)", color: "#fff", fontSize: "0.75rem", fontWeight: "700", padding: "3px 10px", borderRadius: "20px", border: "1px solid rgba(255,193,7,0.4)", flexShrink: 0 },
  badgeStarter: { background: "rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.85)", fontSize: "0.75rem", fontWeight: "600", padding: "3px 10px", borderRadius: "20px", flexShrink: 0 },
  overlay: { position: "fixed", inset: 0, zIndex: 99 },
  tabsBar: { background: "#fff", borderBottom: "1px solid #e8e8e8" },
  tabsContent: { maxWidth: "720px", margin: "0 auto", padding: "0 0.25rem", display: "flex", gap: "0", overflowX: "auto" },
  // Onglets compacts (icône au-dessus du libellé) : les 6 tiennent sur un téléphone
  tab: { flex: "1 1 0", minWidth: "58px", display: "flex", flexDirection: "column", alignItems: "center", gap: "2px", padding: "0.55rem 0.25rem", background: "none", border: "none", cursor: "pointer", fontSize: "0.72rem", fontWeight: "600", color: "#999", borderBottom: "3px solid transparent", whiteSpace: "nowrap" },
  tabActive: { color: "#667eea", borderBottomColor: "#667eea" },
  main: { maxWidth: "1000px", margin: "0 auto", padding: "1.5rem 1rem" },
  card: { background: "#fff", borderRadius: "16px", boxShadow: "0 2px 20px rgba(0,0,0,0.07)", padding: "1.5rem" },
  vide: { textAlign: "center", padding: "2.5rem 1rem" },
  videIcon: { fontSize: "2.5rem", marginBottom: "0.5rem" },
  videTitre: { fontSize: "1.05rem", fontWeight: "700", color: "#1a1a2e", marginBottom: "0.4rem" },
  videTexte: { color: "#888", fontSize: "0.9rem", marginBottom: "1.25rem", lineHeight: 1.6 },
  btnPrimary: { padding: "0.75rem 1.5rem", background: "linear-gradient(135deg,#667eea,#764ba2)", color: "#fff", border: "none", borderRadius: "10px", fontWeight: "700", cursor: "pointer", fontSize: "0.92rem" },
};

const m = {
  overlay: { position: "fixed", inset: 0, background: "rgba(10,15,40,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3000, padding: "1rem" },
  box: { background: "#fff", borderRadius: "18px", padding: "1.5rem", width: "100%", maxWidth: "400px", display: "flex", flexDirection: "column", gap: "6px", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" },
  titre: { margin: "0 0 0.5rem", fontSize: "1.1rem", fontWeight: "800", color: "#1a1a2e" },
  label: { fontSize: "0.8rem", fontWeight: "600", color: "#555", marginTop: "6px" },
  input: { padding: "0.7rem 0.9rem", border: "1.5px solid #e0e0e0", borderRadius: "10px", fontSize: "0.95rem", outline: "none", boxSizing: "border-box", width: "100%" },
  aide: { fontSize: "0.8rem", color: "#888", lineHeight: 1.5, margin: "6px 0 0" },
  erreur: { background: "#ffebee", color: "#b71c1c", borderRadius: "8px", padding: "0.6rem 0.8rem", fontSize: "0.84rem", marginTop: "6px" },
  actions: { display: "flex", gap: "8px", marginTop: "1rem" },
  btnAnnuler: { flex: 1, padding: "0.7rem", background: "#f5f5f5", border: "1px solid #ddd", borderRadius: "10px", cursor: "pointer", fontWeight: "600" },
  btnOk: { flex: 1, padding: "0.7rem", background: "linear-gradient(135deg,#667eea,#764ba2)", color: "#fff", border: "none", borderRadius: "10px", cursor: "pointer", fontWeight: "700" },
};
